export type JobOfferSource = 'MANUAL' | 'MANATAL';

export interface Skill {
  name: string;
  importance: 'must_have' | 'nice_to_have' | 'uncertain';
}

export interface BriefExam {
  technical?: string;
}

/** Recruiter-facing priority levels; the backend derives 0-100 scoring weights from these. */
export type MatchingPriorityLevel = 'low' | 'medium' | 'high' | 'top';

export const MATCHING_PRIORITY_LEVELS: MatchingPriorityLevel[] = ['low', 'medium', 'high', 'top'];

export interface MatchingPriorities {
  yoe: MatchingPriorityLevel;
  skills_match: MatchingPriorityLevel;
  degree: MatchingPriorityLevel;
  sector_experience: MatchingPriorityLevel;
}

export const DEFAULT_MATCHING_PRIORITIES: MatchingPriorities = {
  yoe: 'top',
  skills_match: 'high',
  sector_experience: 'medium',
  degree: 'low',
};

export interface JobOffer {
  id: string;
  title: string;
  description: string;
  educationRequirements?: string;
  skills?: (string | Skill)[];
  codingLanguages?: (string | Skill)[];
  frameworksLibraries?: (string | Skill)[];
  businessKnowledge?: (string | Skill)[];
  softSkills?: (string | Skill)[];
  yearsOfExperience?: number | null;
  customInstructions?: string | null;
  isActive: boolean;
  status: string;
  createdById: string;
  creator_email?: string;
  createdAt: string;
  updatedAt: string | null;
  candidates?: Candidate[];
  documents: Document[];
  remotePolicy?: string;
  contractType?: string;
  location?: string;
  requiredDegree?: string;
  companyName?: string;
  companySector?: string;
  companySize?: number;
  companyDescription?: string;
  mission_role_in_job?: string;
  authorizeCvUpload?: boolean;
  enableCvAutoInterview?: boolean;
  cvInterviewThreshold?: number;
  brief_exam?: BriefExam;
  candidateCount?: number;
  requiresReview?: boolean;
  source?: JobOfferSource;
  matchingPriorities?: MatchingPriorities;
  /** Tenant UI language on public candidate pages. */
  locale?: string;
  /** Workspace branding logo URL on public candidate pages. */
  workspaceLogoUrl?: string;
  /** Optional workspace branding colors for public candidate pages. */
  workspacePrimaryColor?: string | null;
  workspaceSecondaryColor?: string | null;
}

export interface Document {
  id: string;
  jobOfferId: string;
  documentType: string | null;
  fileName: string;
  filePath: string;
  fileSize: number;
  mimeType: string;
  extractedData?: Record<string, unknown>;
  processingStatus: string | null;
  createdAt: string;
  updatedAt?: string;
}

export type CandidateSource = 'cv_upload' | 'talent_pool' | 'talent_cloud';

/** Job-edit rescoring state of a candidate's score; absent = score is current. */
export type CandidateScoringStatus = 'stale' | 'rescoring' | 'failed' | 'quota_blocked';

export interface Candidate {
  id: string;
  talentId: string;
  /** Per-tenant Talent Pool profile this candidate is linked to (drives talent-level notes). */
  talentPoolProfileId?: string;
  jobOfferId: string;
  /** How the candidate entered the job (Source column). Defaults to CV upload. */
  source?: CandidateSource;
  invitationToken?: string;
  /** Job pipeline stage id (Kanban column source of truth). */
  pipelineStageId?: string;
  pipelineStageLabel?: string;
  /** Stage semantic: pending|shortlisted|…|custom */
  pipelineStageSemantic?: string;
  /** LexoRank-style order within the current stage. */
  stagePosition?: string;
  score?: number;
  /** Set while a job edit's rescore is pending/failed/blocked; absent = score is current. */
  scoringStatus?: CandidateScoringStatus;
  enhancedData?: Record<string, unknown>;
  talentName: string;
  talentEmail: string;
  talentLinkedinUrl?: string;
  createdAt: string;
  updatedAt: string;
  hasAssessmentAvailable: boolean;
  hasValidEmail: boolean;
  interviewInvitationToken?: string;
  interviewId?: string;
  interviewStatus?: string;
  interviewExpiresAt?: string;
}

export interface PipelineStage {
  id: string;
  label: string;
  position: number;
  isDefault: boolean;
  stageType: string;
  /** Job-scoped toggle for seeded stage-entry automations. */
  automationEnabled: boolean;
  /**
   * Backend-derived: an active AutomationRule exists for this stage_type.
   * Independent of automationEnabled. False for custom/unseeded types.
   */
  hasAutomationRule: boolean;
  /** Default stage renamed by a recruiter: show `label` as-is, never localize. */
  isLabelCustomized: boolean;
}

export interface JobPipeline {
  id: string;
  jobOfferId: string;
  stages: PipelineStage[];
}
